import {
  Flex,
  ModalAction,
  ModalFooterActions,
  ModalV2,
  Radio,
  RadioGroup,
  spaces,
} from '@electricjs/arc';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AccessConfigurationStepWrapper } from '@/components/People/EmployeeGroups/GroupDetails/AccessConfiguration/AccessConfigurationStepWrapper';
import { useGetApplicationsForGroups } from '@/components/People/EmployeeAppsByGroup/useGetApplicationsForGroups';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useAddEmployeeApplicationAccountsMutation } from '@/redux/slices/employeeApiSlice';
import { Group } from '@/types/groups';

const StyledRadioGroup = styled(RadioGroup)`
  [data-arc='radio'] {
    align-items: flex-start;
  }
`;

type SkipApplicationSetupModalProps = {
  employeeId: string;
  employeeGroups: Group[];
  excludedAppIds?: string[];
  onClose: () => void;
  onComplete: () => void;
};

export const SkipApplicationSetupModal = ({
  employeeId,
  employeeGroups,
  excludedAppIds = [],
  onClose,
  onComplete,
}: SkipApplicationSetupModalProps) => {
  const { t } = useTranslation('common');
  const organizationId = useGetOrganizationId();
  const { showErrorToast } = useGlobalUI();
  const { space_m, space_s } = spaces;
  const [addApplications, setAddApplications] = useState<boolean | null>(null);

  const { data: appsByGroup } = useGetApplicationsForGroups(employeeGroups);
  const applicationIds = useMemo(
    () => (appsByGroup ?? []).flatMap(ag => ag.applications.map(app => app.id)),
    [appsByGroup]
  );
  const excludedSet = useMemo(() => new Set(excludedAppIds), [excludedAppIds]);
  const uniqueApplicationIds = useMemo(
    () =>
      Array.from(new Set(applicationIds)).filter(id => !excludedSet.has(id)),
    [applicationIds, excludedSet]
  );

  const [addEmployeeApplicationAccounts, { isLoading: isAddingApplications }] =
    useAddEmployeeApplicationAccountsMutation();

  const isAddButtonDisabled = addApplications === null;

  /**
   * Runs when the user clicks Finish in the Skip Application Setup modal.
   *
   * Behavior depends on the selected option:
   * - "Employee already has access": Adds the applications from the selected
   *   groups to the employee profile with skipProvisioning: true (no provisioning
   *   requests; same as the "Has Account" path in group application access).
   * - "Employee will receive access later": Does not add applications; only
   *   completes the flow.
   *
   * In both cases the modal is closed and onComplete is called (e.g. handleSkipOnboarding
   * in Applications), which adds the employee to groups and navigates without
   * creating onboarding requests.
   *
   */
  const handleFinish = async () => {
    try {
      if (addApplications === true && uniqueApplicationIds.length > 0) {
        await addEmployeeApplicationAccounts({
          organizationId,
          employeeId,
          applicationIds: uniqueApplicationIds,
          skipProvisioning: true,
        }).unwrap();
      }
      onClose();
      onComplete();
    } catch (error) {
      window?.DD_RUM?.addError(
        `Error adding applications to employee ${employeeId}`,
        { location: 'SkipApplicationSetupModal' }
      );
      showErrorToast({
        id: 'skip-application-setup-error-toast',
        message: t('addApplicationsToEmployee.errorToast'),
        stack: true,
      });
    }
  };

  return (
    <ModalV2
      minWidth="70rem"
      visible
      hide={onClose}
      ariaLabelledby="skip-application-setup-modal">
      <AccessConfigurationStepWrapper
        title={t('onboardings.applications.skipApplicationSetupModal.title')}
        description=""
        footerActions={
          <ModalFooterActions>
            <ModalAction
              id="skip-application-setup-modal-cancel"
              buttonSize="large"
              variant="text"
              onClick={onClose}>
              {t('onboardings.applications.skipApplicationSetupModal.cancel')}
            </ModalAction>
            <ModalAction
              id="skip-application-setup-modal-add-applications"
              buttonSize="large"
              onClick={handleFinish}
              loading={isAddingApplications}
              disabled={isAddButtonDisabled}>
              {t(
                'onboardings.applications.skipApplicationSetupModal.addApplicationsButton'
              )}
            </ModalAction>
          </ModalFooterActions>
        }>
        <Flex flexDirection="column" gap={space_s} width="100%">
          <StyledRadioGroup
            flexDirection="column"
            flexWrap="wrap"
            gap={space_m}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setAddApplications(e.target.value === 'yes');
            }}>
            <Radio
              id="skip-application-setup-add-applications"
              value="yes"
              checked={addApplications === true}>
              {t(
                'onboardings.applications.skipApplicationSetupModal.addApplications'
              )}
            </Radio>
            <Radio
              id="skip-application-setup-do-not-add"
              value="no"
              checked={addApplications === false}>
              {t(
                'onboardings.applications.skipApplicationSetupModal.doNotAddApplications'
              )}
            </Radio>
          </StyledRadioGroup>
        </Flex>
      </AccessConfigurationStepWrapper>
    </ModalV2>
  );
};
